import React, { useRef, useState, useEffect } from "react";
import "./CameraApp.css";
import TransparentHeader from "../../components/TransparentHeader";
import SwipeableEdgeDrawer from "../../components/BottomDrawer";
import ImageDisplay from "../SnapCamera/ImageDisplay";
import { useConfig } from "../../context/ConfigProvider";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import NewImageDisplay from "./NewImageDisplay";
import DynamicIconGroup from "../../components/DynamicIconGroup";
import NewBottonDrawer from "../../components/NewBottonDrawer";
import FirstPartyData from "../../components/FirstPartyData";
import NotesIcon from "@mui/icons-material/Notes";
import ChatOverlay from "../../components/ChatOverlay";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ResponsiveMedia from "../../components/ResponsiveImage";
import Overlay from "../../components/Overlay";
import { MoreVert } from "@mui/icons-material";
import AssignmentIcon from "../../assets/assignment_icon.png";
import SuccessNotification from "../../components/SuccessNotification";
import { isMobileDevice } from "../../utils/deviceDetection";
import CaptureComponent from "../../components/CaptureComponent";

const CameraApp = ({
  cKey,
  oId,
  onClose,
  showBottomBar,
  config: parentConfig,
  multiExp,
  onFirstPartyToggle,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [prompt, setPrompt] = useState(null);
  const [open, setOpen] = useState(false);
  const [customerKey, setCustomerKey] = useState(0);
  const [ocid, setOcid] = useState(0);
  const [configFetched, setConfigFetched] = useState(false);
  const [icons, setIcons] = useState([]);
  const [Draweropen, setDrawerOpen] = useState(false);
  const [showFirstParty, setShowFirstParty] = useState(false);
  const [userTriggered, setUserTriggered] = useState(false);
  const [brandId, setBrandId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [delay, setDelay] = useState(99999);
  const [cameraOn, setCameraOn] = useState(true);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [modelConfig, setModelConfig] = useState({
    customerKey: "",
    ocid: "",
    query: "",
    model: "",
    threshold: "",
    context_prompt: "",
    record_size: 100,
    defaultPrompt: "",
    imagePrompt: "",
    language: "english",
    langGraph: false,
  });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showZoom, setShowZoom] = useState(false);
  const [showMessage, setShowMessage] = useState(true);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  console.debug("Is this a mobile device? ", isMobileDevice());

  const { config, fetchConfig, loading, error } = useConfig();

  const effectiveConfig = parentConfig || config?.userData;

  const toggleCamera = async () => {
    if (cameraOn) {
      // Stop the camera feed
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
    } else {
      // Start the camera feed
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        videoRef.current.srcObject = stream;
      } catch (err) {
        console.error("Error accessing camera: ", err);
      }
    }
    setCameraOn(!cameraOn);
  };

  useEffect(() => {
    // function to prevent zooming behavior
    const preventZoom = (event) => {
      if (event.type.startsWith("gesture") && event.scale !== 1) {
        event.preventDefault();
      }
    };
    // likely to work on webkit based browsers (IOS safari)
    document.addEventListener("gesturestart", preventZoom);
    document.addEventListener("gesturechange", preventZoom);
    document.addEventListener("gestureend", preventZoom);
    // cleaning up the event listeners to prevent memory leaks
    return () => {
      document.removeEventListener("gesturestart", preventZoom);
      document.removeEventListener("gesturechange", preventZoom);
      document.removeEventListener("gestureend", preventZoom);
    };
  }, []);

  const getCameraStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const videoElement = videoRef.current;
      videoElement.srcObject = stream;

      const track = stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();

      // Start touch zoom logic
      let lastTouchDistance = null;
      let currentZoom = capabilities.zoom?.min || 1;

      videoElement.addEventListener("touchmove", (event) => {
        if (event.touches.length === 2) {
          const [touch1, touch2] = event.touches;
          const currentDistance = Math.hypot(
            touch2.pageX - touch1.pageX,
            touch2.pageY - touch1.pageY
          );

          if (lastTouchDistance !== null) {
            const zoomChange = (currentDistance - lastTouchDistance) * 0.05;
            currentZoom = Math.min(
              Math.max(currentZoom + zoomChange, capabilities.zoom?.min || 1),
              capabilities.zoom?.max || currentZoom + zoomChange
            );
            track
              .applyConstraints({
                advanced: [{ zoom: currentZoom }],
              })
              .catch((err) => console.error("Zoom error:", err));

            setZoomLevel(currentZoom);
            setShowZoom(true);

            setTimeout(() => setShowZoom(false), 1000);
          }

          lastTouchDistance = currentDistance;
        }
      });

      videoElement.addEventListener("touchend", () => {
        lastTouchDistance = null;
      });
    } catch (err) {
      console.error("Error accessing camera: ", err);
    }
  };

  useEffect(() => {
    getCameraStream();
  }, []);
  useEffect(() => {
    getCameraStream();
  }, [cameraOn]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        (!videoRef.current || !videoRef.current.srcObject)
      ) {
        getCameraStream();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!configFetched) {
      const params = new URLSearchParams(window.location.search);
      const chatbotParam = params.get("chatbot");
      let urlCKey, urlOcid;
      if (chatbotParam) {
        [urlCKey, urlOcid] = chatbotParam.split("x");
      }
      const finalCKey = cKey || urlCKey;
      const finalOcid = oId || urlOcid;

      if (finalCKey && finalOcid) {
        setCustomerKey(parseInt(finalCKey, 10));
        setOcid(parseInt(finalOcid, 10));
        fetchConfig(finalCKey, finalOcid);
        setConfigFetched(true);
      } else {
        console.warn("Customer key and/or OCID not provided.");
      }
    }
  }, [configFetched, fetchConfig, cKey, oId]);

  useEffect(() => {
    if (effectiveConfig) {
      setModelConfig((prevState) => ({
        ...prevState,
        context_prompt: effectiveConfig?.contextprompt || "",
        customerKey,
        ocid,
        model: effectiveConfig?.gptversion || "",
        threshold: effectiveConfig?.threshold || 0.7,
        defaultPrompt: effectiveConfig?.defaultpromt || "",
        imagePrompt: effectiveConfig?.field1,
        record_size: effectiveConfig?.recordsize,
        chatbotTitle: effectiveConfig?.userData?.title,
        chainModel: effectiveConfig?.chain_model,
        attachEnabled: effectiveConfig?.attachEnabled,
        translateEnabled: effectiveConfig?.translateEnabled,
        compressionEnabled: effectiveConfig?.compressionEnabled,
        compressionQuality: effectiveConfig?.compressionQuality,
        maxTokens: effectiveConfig?.max_tokens,
        langGraph: effectiveConfig?.langgraph,
        background: effectiveConfig?.background,
        language: prevState.language,
      }));
      setBrandId(effectiveConfig?.brandid);
      setBatchId(effectiveConfig?.batchid);
      const delayValue = parseInt(effectiveConfig?.field3, 10);
      setDelay(isNaN(delayValue) ? 99999 : delayValue);

      const icons = [
        {
          image: effectiveConfig?.cfield1 ? effectiveConfig?.cfield1 : "",
          alt: "icon",
          onClick: () =>
            window.open(
              effectiveConfig?.cfield2
                ? effectiveConfig?.cfield2
                : "https://fiduciaai.com"
            ),
        },
        {
          image: effectiveConfig?.cfield3 ? effectiveConfig?.cfield3 : "",
          alt: "icon",
          onClick: () =>
            window.open(
              effectiveConfig?.cfield4
                ? effectiveConfig?.cfield4
                : "https://fiduciaai.com"
            ),
        },
        {
          image: effectiveConfig?.cfield5 ? effectiveConfig?.cfield5 : "",
          alt: "icon",
          onClick: () =>
            window.open(
              effectiveConfig?.cfield6
                ? effectiveConfig?.cfield6
                : "https://fiduciaai.com"
            ),
        },
        {
          image: effectiveConfig?.cfield7 ? effectiveConfig?.cfield7 : "",
          alt: "icon",
          onClick: () =>
            window.open(
              effectiveConfig?.cfield8
                ? effectiveConfig?.cfield8
                : "https://fiduciaai.com"
            ),
        },
        {
          image: effectiveConfig?.cfield9 ? effectiveConfig?.cfield9 : "",
          alt: "icon",
          onClick: () =>
            window.open(
              effectiveConfig?.cfield10
                ? effectiveConfig?.cfield10
                : "https://fiduciaai.com"
            ),
        },
      ];

      setIcons(icons);
      setConfigLoaded(true);
    }
  }, [effectiveConfig, customerKey, ocid, cKey, oId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!userTriggered) {
        setShowFirstParty(true);
        if (onFirstPartyToggle) {
          onFirstPartyToggle(true);
        }
      }
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [configLoaded, userTriggered, delay]);

  // The onImageUpload callback for file uploads.
  const handleFileUpload = (file) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      console.debug("File uploaded:", imageUrl);
    }
  };

  const handleLanguageChange = (newLanguage) => {
    setModelConfig((prev) => ({
      ...prev,
      language: newLanguage,
    }));
  };

  const handleToggleOverlay = () => {
    console.debug("Toggle overlay clicked");
    setIsOverlayOpen(!isOverlayOpen);
  };

  return (
    <div className="camera-app">
      {showFirstParty && (
        <FirstPartyData
          customerKey={customerKey}
          batchId={batchId}
          brandId={brandId}
          onClose={() => {
            setShowFirstParty(false);
            if (onFirstPartyToggle) {
              onFirstPartyToggle(false);
            }
          }}
          setShowSuccessNotification={setShowSuccessNotification}
        />
      )}

      <SuccessNotification
        open={showSuccessNotification}
        onCloseNotification={() => setShowSuccessNotification(false)}
      />

      <CaptureComponent
        videoRef={videoRef}
        canvasRef={canvasRef}
        modelConfig={modelConfig}
        logoUrl={effectiveConfig?.logo}
        titleHeader={effectiveConfig?.title}
        config={effectiveConfig}
        text={modelConfig?.imagePrompt}
        imagePrompt={modelConfig?.imagePrompt}
        onImageUpload={handleFileUpload}
        usePortal={multiExp || false}
        onClose={() => {
          setOpen(false);
          sessionStorage.removeItem("chatMessages");
        }}
      >
        {({ capturePhoto, hasCapturedImage }) =>
          !hasCapturedImage ? (
            <>
              <TransparentHeader
                logoUrl={effectiveConfig?.logo}
                titleHeader={effectiveConfig?.title}
                leftIconCallbackMethod={onClose}
                showLeftIcon={!!onClose}
              />

              {showMessage && (
                <div className="message-overlay">
                  <div>
                    {effectiveConfig?.cfield12 ? (
                      <img
                        src={effectiveConfig?.cfield12}
                        alt="Donate"
                        style={{ width: "20px", height: "20px" }}
                      />
                    ) : (
                      " ℹ️"
                    )}
                  </div>
                  <div>
                    {effectiveConfig?.cfield11
                      ? effectiveConfig?.cfield11
                      : "Snap the moment and level up your game!"}
                  </div>
                </div>
              )}

              {/* conditionally render dynamic icon group based on first party data, because it is conflicting with the overlayed form  */}
              {!showFirstParty && <DynamicIconGroup icons={icons} />}

              {isMobileDevice() && cameraOn && (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="video"
                  />
                  <canvas ref={canvasRef} className="hidden" />

                  <div className="overlay">
                    <button
                      className="capture-button"
                      onClick={capturePhoto}
                    ></button>
                  </div>
                </>
              )}
              {(!isMobileDevice() || !cameraOn) && (
                <ResponsiveMedia
                  src={
                    modelConfig?.background ||
                    "https://ceimages.s3.amazonaws.com/43502810/signal-2025-04-29-221413_002.jpeg"
                  }
                  alt="Camera Off"
                  style={{ width: "100%", height: "auto", display: "block" }}
                />
              )}

              {isMobileDevice() && (
                <button className="toggle-camera-button" onClick={toggleCamera}>
                  {cameraOn ? <NoPhotographyIcon /> : <CameraAltIcon />}
                </button>
              )}

              <Overlay isOpen={isOverlayOpen} onClose={handleToggleOverlay} />
              {/* boundingbox and playerdescription components can be passed as props above in the overlay component */}

              <div className="capture-container">
                {delay !== 99999 ? (
                  <button
                    className="first-party-fab"
                    onClick={() => {
                      setUserTriggered(true);
                      setShowFirstParty(true);
                      if (onFirstPartyToggle) {
                        onFirstPartyToggle(true);
                      }
                    }}
                    title="Fill Form"
                  >
                    <img
                      src={AssignmentIcon}
                      style={{
                        width: "35px",
                        height: "35px",
                        marginLeft: "6px",
                        marginBottom: "4px",
                      }}
                      alt="Assignment Icon"
                    />
                  </button>
                ) : null}

                {isMobileDevice() && (
                  <div className="camera-icons">
                    <CameraAltIcon
                      className="camera-icon"
                      onClick={capturePhoto}
                    />
                    <NoPhotographyIcon
                      className="camera-icon"
                      onClick={toggleCamera}
                    />
                  </div>
                )}
              </div>
              <ChatOverlay
                timestamp={new Date().getTime()}
                usePortal={multiExp || false}
                modelConfig={modelConfig}
                image={undefined}
                config={effectiveConfig}
                enablePrompts={false}
                hasBottomBar={showBottomBar}
                onImageUpload={handleFileUpload}
                onLanguageChange={handleLanguageChange}
              />

              {showZoom && (
                <div className="zoom-level-display">
                  <span>{zoomLevel.toFixed(2) + "x"}</span>
                </div>
              )}
            </>
          ) : null
        }
      </CaptureComponent>
    </div>
  );
};

export default CameraApp;
